import React from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  selector as verifyTokenSelector,
  verifyToken,
} from "../orgAdminEnrollment/verifyTokenSlice";
import { useSearchParams } from "react-router-dom";
import { useNavigate } from "react-router";
import useNotification from "../../components/notification/useNotification";
import { updateQueryParams } from "../orgAdminEnrollment/enrollOrganizationSlice";
import { Status } from "../../common/constants";
import {
  resetPassword,
  selector as resetPasswordSelector,
} from "./resetPasswordSlice";

const useResetPassword = () => {
  const dispatch: any = useDispatch();
  const navigate = useNavigate();
  const { showSuccessNotification, showWarningNotification } =
    useNotification();
  const [isVerified, setIsVerified] = React.useState<boolean>(false);
  const [organizationName, setOrganizationName] = React.useState<string>("");
  const [adminName, setAdminName] = React.useState<string>("");
  const [email, setEmail] = React.useState<string>("");
  const [password, setPassword] = React.useState<string>("");
  const [confirmPassword, setConfirmPassword] = React.useState<string>("");
  const [passwordError, setPasswordError] = React.useState<string>("");
  const [isValidPassword, setisValidPassword] = React.useState<boolean>(false);
  const [confirmPasswordError, setConfirmPasswordError] = React.useState<string>("");
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [searchParams] = useSearchParams();
  const verifyTokenApiResponse = useSelector(verifyTokenSelector);
  const resetPasswordResponse = useSelector(resetPasswordSelector);

  React.useEffect(() => {
    const token = searchParams?.get("token");
    dispatch(updateQueryParams(token));
  }, [searchParams, dispatch]);

  React.useEffect(() => {
    if (
      resetPasswordResponse.status === Status.PENDING ||
      verifyTokenApiResponse.status === Status.PENDING
    ) {
      setIsLoading(true);
    } else {
      setIsLoading(false);
    }
  }, [resetPasswordResponse.status, verifyTokenApiResponse.status]);

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newpassword = e.target.value;

    setPassword(newpassword);

    const passwordValid = validatePassword(newpassword);
    
    if(confirmPassword.trim()){
      const confirmValid = validateConfirmPassword(confirmPassword, newpassword);
      setisValidPassword(passwordValid && confirmValid);
    } 
  };

  const handleConfirmPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newpassword = e.target.value;

    setConfirmPassword(newpassword);

    const confirmValid = validateConfirmPassword(
      newpassword,
      password
    );

    const passwordValid = validatePassword(password);

    setisValidPassword(passwordValid && confirmValid);
  };

  const validatePassword = (newpassword: string): boolean => {
    const passwordRegex =/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&]).{8,}$/;

    if (!newpassword.trim()) {
      setPasswordError("Password is required.");
      return false;
    }

    if (!passwordRegex.test(newpassword)) {
      setPasswordError("Password must be at least 8 characters long and include uppercase, lowercase, number, and special character.");
      return false;
    }

    setPasswordError("");
    return true;
  };

  const validateConfirmPassword = (newPassword: string, currentPassword: string): boolean => {
    if (!newPassword.trim()) {
      setConfirmPasswordError("Confirm Password is required.");
      return false;
    }

    if (newPassword !== currentPassword) {
      setConfirmPasswordError("Passwords do not match.");
      return false;
    }

    setConfirmPasswordError("");
    return true;
  };

  const submitResetPassword = async (e: React.FormEvent) => {
      e.preventDefault()
      await dispatch(resetPassword({ email, password }));
  };

  const handleVerifyToken = React.useCallback(async () => {
    const verificationToken = new URLSearchParams(window.location.search).get(
      "token"
    );
    if (verificationToken && !isVerified) {
      dispatch(verifyToken(verificationToken));
    }
  }, [dispatch, isVerified]);

  React.useEffect(() => {
    if (resetPasswordResponse.status === Status.SUCCESS) {
      setEmail("");
      setAdminName("");
      navigate("/", { state: { successMessage: "Password Reset Successful" } });
    } else if (resetPasswordResponse.status === Status.FAILED) {
      setPassword("");
      setConfirmPassword("");
      showWarningNotification(resetPasswordResponse.error || "Something went wrong!");
    }
  }, [resetPasswordResponse.status, resetPasswordResponse.error]);

  React.useEffect(() => {
    if (verifyTokenApiResponse.status === Status.SUCCESS) {
      setIsVerified(true);
      setOrganizationName(verifyTokenApiResponse.adminDetails.organization);
      setEmail(verifyTokenApiResponse.adminDetails.email);
      setAdminName(verifyTokenApiResponse.adminDetails.name);
    }
  }, [verifyTokenApiResponse.status]);

  React.useEffect(() => {
    handleVerifyToken();
  }, [handleVerifyToken]);

  return {
    organizationName,
    adminName,
    email,
    password,
    confirmPassword,
    passwordError,
    confirmPasswordError,
    handlePasswordChange,
    handleConfirmPasswordChange,
    submitResetPassword,
    isLoading,
    setIsLoading,
    isValidPassword
  };
};

export default useResetPassword;
